// 2D affine matrices [a, b, c, d, e, f] (x' = a·x + c·y + e, y' = b·x + d·y + f), as canvas / SVG / PDF.
import type { Mat2D, Rect } from '../model/types';

export const I: Mat2D = [1, 0, 0, 1, 0, 0];

/** m · n (apply n first, then m). */
export function mul(m: Mat2D, n: Mat2D): Mat2D {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function inv(m: Mat2D): Mat2D {
  const det = m[0] * m[3] - m[1] * m[2];
  if (Math.abs(det) < 1e-12) return [...I] as Mat2D;
  const a = m[3] / det,
    b = -m[1] / det,
    c = -m[2] / det,
    d = m[0] / det;
  return [a, b, c, d, -(a * m[4] + c * m[5]), -(b * m[4] + d * m[5])];
}

export const apply = (m: Mat2D, x: number, y: number): [number, number] => [
  m[0] * x + m[2] * y + m[4],
  m[1] * x + m[3] * y + m[5],
];
export const translate = (tx: number, ty: number): Mat2D => [1, 0, 0, 1, tx, ty];
export const scale = (sx: number, sy = sx): Mat2D => [sx, 0, 0, sy, 0, 0];
export const rotate = (rad: number): Mat2D => {
  const c = Math.cos(rad),
    s = Math.sin(rad);
  return [c, s, -s, c, 0, 0];
};
/** Horizontal shear by angle (x' = x + tan·y). */
export const shear = (rad: number): Mat2D => [1, 0, Math.tan(rad), 1, 0, 0];

/** `m` applied about the point (px, py). */
export const about = (m: Mat2D, px: number, py: number): Mat2D =>
  mul(translate(px, py), mul(m, translate(-px, -py)));

/** Translation, rotation (rad), scale and shear (rad) of an affine matrix (rotation first in the decomposition). */
export function decompose(m: Mat2D): {
  tx: number;
  ty: number;
  rotation: number;
  sx: number;
  sy: number;
  shear: number;
} {
  const [a, b, c, d, e, f] = m;
  const sx = Math.hypot(a, b);
  const rotation = Math.atan2(b, a);
  const det = a * d - b * c;
  const sy = sx ? det / sx : 0;
  const shearRad = sx ? Math.atan2(a * c + b * d, sx * sx) : 0;
  return { tx: e, ty: f, rotation, sx, sy, shear: shearRad };
}

/** Axis-aligned bounds of `r` after `m`. */
export function mapRect(m: Mat2D, r: Rect): Rect {
  const pts = [
    apply(m, r.x, r.y),
    apply(m, r.x + r.w, r.y),
    apply(m, r.x, r.y + r.h),
    apply(m, r.x + r.w, r.y + r.h),
  ];
  const xs = pts.map((p) => p[0]),
    ys = pts.map((p) => p[1]);
  const x = Math.min(...xs),
    y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function unionRect(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b;
  if (!b) return a;
  const x = Math.min(a.x, b.x),
    y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

export const isIdentity = (m: Mat2D) =>
  m[0] === 1 && m[1] === 0 && m[2] === 0 && m[3] === 1 && m[4] === 0 && m[5] === 0;
